import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Truck, AlertTriangle, ArrowRightLeft, Activity, Gauge, Wrench,
  Loader2, ChevronRight, BarChart3, RefreshCcw, FileText,
} from "lucide-react";

const FMT = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES").format(n));

function relDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  const diffH = Math.floor((Date.now() - d.getTime()) / 3600000);
  if (diffH < 1) return "hace minutos";
  if (diffH < 24) return `hace ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 30) return `hace ${diffD}d`;
  return d.toLocaleDateString("es-ES");
}

export default function FlotaDashboard() {
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [partes, setPartes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partesLoading, setPartesLoading] = useState(false);

  // Filtros del listado de partes
  const [pVehiculo, setPVehiculo] = useState("__all__");
  const [pEstado, setPEstado] = useState("__all__");
  const [pIncidencia, setPIncidencia] = useState("__all__");
  const [pDesde, setPDesde] = useState("");
  const [pHasta, setPHasta] = useState("");

  const cargarDashboard = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/flota/dashboard?dias=${dias}`);
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  const cargarPartes = async () => {
    setPartesLoading(true);
    try {
      const params = new URLSearchParams();
      if (pVehiculo && pVehiculo !== "__all__") params.set("vehiculo_id", pVehiculo);
      if (pEstado && pEstado !== "__all__") params.set("estado", pEstado);
      if (pIncidencia === "si") params.set("incidencia", "true");
      if (pIncidencia === "no") params.set("incidencia", "false");
      if (pDesde) params.set("desde", pDesde);
      if (pHasta) params.set("hasta", pHasta);
      params.set("limit", "100");
      const { data: r } = await api.get(`/flota/partes?${params.toString()}`);
      setPartes(r.items || []);
    } finally {
      setPartesLoading(false);
    }
  };

  useEffect(() => { cargarDashboard(); }, [dias]);
  useEffect(() => { cargarPartes(); }, [pVehiculo, pEstado, pIncidencia, pDesde, pHasta]);

  const kpis = data?.kpis;
  const vehiculos = data?.vehiculos || [];
  const cambios = data?.cambios || [];

  const vehiculoLabelById = useMemo(() => {
    const m = {};
    vehiculos.forEach((v) => { m[v.id] = `${v.nombre}${v.matricula ? ` (${v.matricula})` : ""}`; });
    return m;
  }, [vehiculos]);

  if (loading) {
    return (
      <div className="p-6 text-center text-zinc-500" data-testid="flota-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500 inline" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6" data-testid="flota-dashboard">
      <PageHeader
        title="Dashboard de Flota"
        subtitle={`Asignados vs realmente usados · Cambios in-situ · Incidencias · Últimos ${dias} días`}
        icon={BarChart3}
      >
        <div className="flex items-center gap-2">
          <Select value={String(dias)} onValueChange={(v) => setDias(parseInt(v))}>
            <SelectTrigger className="w-36" data-testid="select-rango-dias">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Últimos 7 días</SelectItem>
              <SelectItem value="30">Últimos 30 días</SelectItem>
              <SelectItem value="90">Últimos 90 días</SelectItem>
              <SelectItem value="180">Últimos 6 meses</SelectItem>
              <SelectItem value="365">Último año</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={cargarDashboard} data-testid="btn-refrescar">
            <RefreshCcw className="w-4 h-4 mr-1" /> Refrescar
          </Button>
        </div>
      </PageHeader>

      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="kpis-globales">
        <KpiCard icon={Truck} label="Vehículos activos" value={kpis?.total_vehiculos} sub={`${kpis?.vehiculos_disponibles || 0} disponibles`} />
        <KpiCard icon={Activity} label="En uso ahora" value={kpis?.vehiculos_con_parte_abierto} color={kpis?.vehiculos_con_parte_abierto > 0 ? "emerald" : "zinc"} />
        <KpiCard icon={Gauge} label="Km recorridos" value={FMT(kpis?.total_km_recorridos)} unit="km" />
        <KpiCard icon={FileText} label="Partes" value={kpis?.total_partes} />
        <KpiCard icon={ArrowRightLeft} label="Cambios in-situ" value={kpis?.total_cambios_vehiculo}
          color={kpis?.total_cambios_vehiculo > 0 ? "amber" : "zinc"}
          sub={kpis?.vehiculos_problematicos > 0 ? `${kpis.vehiculos_problematicos} problemáticos` : null} />
        <KpiCard icon={AlertTriangle} label="Incidencias" value={kpis?.total_incidencias}
          color={kpis?.total_incidencias > 0 ? "rose" : "emerald"} />
      </div>

      {/* Tabla Asignados vs Usados */}
      <section className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="seccion-vehiculos">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <Truck className="w-4 h-4 text-amber-400" /> Asignados vs Realmente Usados
          </h2>
          <span className="text-xs text-zinc-500">Ordenado por nº de cambios in-situ ↓</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950/50">
              <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                <th className="px-3 py-2">Vehículo</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-right">Rutas asignadas</th>
                <th className="px-3 py-2 text-right">Partes uso</th>
                <th className="px-3 py-2 text-right">Km</th>
                <th className="px-3 py-2 text-right">Cambios in-situ</th>
                <th className="px-3 py-2 text-right">Incidencias</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {vehiculos.map((v) => (
                <tr key={v.id} className="border-t border-zinc-800/50 hover:bg-zinc-800/30 transition" data-testid={`fila-vehiculo-${v.id}`}>
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-zinc-100">{v.nombre}</div>
                    {v.matricula && <div className="text-[10px] font-mono text-zinc-500">{v.matricula}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <EstadoBadge estado={v.estado} parteAbierto={v.parte_abierto} />
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{v.rutas_asignadas}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{v.partes_uso}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{FMT(v.km_recorridos)}</td>
                  <td className="px-3 py-2.5 text-right">
                    {v.total_cambios > 0 ? (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs ${
                        v.total_cambios >= 3 ? "bg-rose-500/20 text-rose-300" :
                        v.total_cambios >= 2 ? "bg-amber-500/20 text-amber-300" :
                        "bg-zinc-700/40 text-zinc-300"
                      }`}>
                        <ArrowRightLeft className="w-3 h-3" />
                        {v.total_cambios}
                        <span className="opacity-60 text-[10px]">
                          ({v.cambios_entrantes}↗/{v.cambios_salientes}↘)
                        </span>
                      </span>
                    ) : (
                      <span className="text-zinc-600">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {v.incidencias > 0 ? (
                      <span className="inline-flex items-center gap-1 bg-rose-500/20 text-rose-300 px-2 py-0.5 rounded text-xs">
                        <AlertTriangle className="w-3 h-3" /> {v.incidencias}
                      </span>
                    ) : (
                      <span className="text-zinc-600">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <Link to={`/vehiculos/${v.id}`} className="text-amber-400 hover:text-amber-300 text-xs inline-flex items-center gap-0.5"
                      data-testid={`btn-ver-vehiculo-${v.id}`}>
                      Detalle <ChevronRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
              {vehiculos.length === 0 && (
                <tr><td colSpan="8" className="text-center text-zinc-500 italic py-6">Sin vehículos activos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Histórico cambios de vehículo */}
      <section className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="seccion-cambios">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="font-bold text-sm flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4 text-amber-400" /> Cambios de vehículo durante ruta · últimos {dias} días
          </h2>
          <span className="text-xs text-zinc-500">{cambios.length} eventos</span>
        </div>
        {cambios.length === 0 ? (
          <div className="text-center text-zinc-500 italic py-6 text-sm">
            🎉 Sin cambios in-situ en este período · Buena planificación
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/60">
            {cambios.map((c, i) => (
              <div key={i} className="px-4 py-3 hover:bg-zinc-800/20 flex items-start justify-between gap-3 flex-wrap"
                data-testid={`cambio-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/rutas/${c.ruta_id}`} className="font-mono text-xs text-amber-400 hover:text-amber-300">
                      {c.ruta_codigo || c.ruta_id?.slice(0, 8)}
                    </Link>
                    <span className="text-[10px] text-zinc-500">{c.ruta_fecha?.slice(0, 10)}</span>
                    {c.ruta_estado && (
                      <span className="text-[10px] bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded uppercase">
                        {c.ruta_estado}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-sm flex items-center gap-2 flex-wrap">
                    <span className="text-zinc-300 line-through opacity-60">{c.from || "—"}</span>
                    <ArrowRightLeft className="w-3 h-3 text-amber-500" />
                    <span className="text-zinc-100 font-semibold">{c.to || "—"}</span>
                  </div>
                  {c.motivo && (
                    <div className="mt-1 text-xs text-amber-200 italic">💬 {c.motivo}</div>
                  )}
                  <div className="mt-1 text-[10px] text-zinc-500">
                    {c.by_nombre || "—"} · {relDate(c.at)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Listado partes con filtros */}
      <section className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="seccion-partes">
        <div className="px-4 py-3 border-b border-zinc-800">
          <h2 className="font-bold text-sm flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-amber-400" /> Partes de Vehículo
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <Select value={pVehiculo} onValueChange={setPVehiculo}>
              <SelectTrigger data-testid="filtro-partes-vehiculo">
                <SelectValue placeholder="Vehículo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los vehículos</SelectItem>
                {vehiculos.map((v) => (
                  <SelectItem key={v.id} value={v.id}>{v.nombre}{v.matricula ? ` (${v.matricula})` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pEstado} onValueChange={setPEstado}>
              <SelectTrigger data-testid="filtro-partes-estado">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Todos los estados</SelectItem>
                <SelectItem value="abierto">Solo abiertos</SelectItem>
                <SelectItem value="cerrado">Solo cerrados</SelectItem>
              </SelectContent>
            </Select>
            <Select value={pIncidencia} onValueChange={setPIncidencia}>
              <SelectTrigger data-testid="filtro-partes-incidencia">
                <SelectValue placeholder="Incidencias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Con o sin incidencias</SelectItem>
                <SelectItem value="si">Solo con incidencia</SelectItem>
                <SelectItem value="no">Solo sin incidencia</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={pDesde} onChange={(e) => setPDesde(e.target.value)} placeholder="Desde" data-testid="filtro-partes-desde" />
            <Input type="date" value={pHasta} onChange={(e) => setPHasta(e.target.value)} placeholder="Hasta" data-testid="filtro-partes-hasta" />
          </div>
        </div>
        {partesLoading ? (
          <div className="text-center text-zinc-500 py-8"><Loader2 className="w-6 h-6 animate-spin text-amber-500 inline" /></div>
        ) : partes.length === 0 ? (
          <div className="text-center text-zinc-500 italic py-6 text-sm">Sin partes con esos filtros</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/50">
                <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                  <th className="px-3 py-2">Nº</th>
                  <th className="px-3 py-2">Vehículo</th>
                  <th className="px-3 py-2">Conductor</th>
                  <th className="px-3 py-2">Salida</th>
                  <th className="px-3 py-2 text-right">Km</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                  <th className="px-3 py-2 text-center">Inc.</th>
                  <th className="px-3 py-2">Ruta</th>
                </tr>
              </thead>
              <tbody>
                {partes.map((p) => (
                  <tr key={p.id} className="border-t border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`parte-${p.id}`}>
                    <td className="px-3 py-2 font-mono text-amber-300 text-xs">{p.numero}</td>
                    <td className="px-3 py-2">
                      {p.vehiculo?.nombre || vehiculoLabelById[p.vehiculo_id] || p.vehiculo_id?.slice(0, 8)}
                      {p.vehiculo?.matricula && <span className="text-[10px] text-zinc-500 ml-1">({p.vehiculo.matricula})</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{p.conductor_nombre || "—"}</td>
                    <td className="px-3 py-2 text-xs text-zinc-400">{p.fecha_salida?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs">{p.km_recorridos != null ? FMT(p.km_recorridos) : "—"}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase font-semibold ${
                        p.estado === "abierto" ? "bg-emerald-500/30 text-emerald-200" : "bg-zinc-700/50 text-zinc-400"
                      }`}>{p.estado}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {p.incidencia_tuvo ? <AlertTriangle className="w-4 h-4 text-rose-400 inline" /> : <span className="text-zinc-600">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {p.ruta_id ? (
                        <Link to={`/rutas/${p.ruta_id}`} className="text-amber-400 hover:text-amber-300">
                          Ver →
                        </Link>
                      ) : (
                        <span className="text-zinc-500 italic">{p.uso || "manual"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function KpiCard({ icon: Ico, label, value, unit, color, sub }) {
  const colors = {
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-200",
    emerald: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-200",
    zinc: "border-zinc-700 bg-zinc-900/40 text-zinc-300",
  };
  return (
    <div className={`border rounded-lg p-3 ${colors[color] || colors.zinc}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70 flex items-center gap-1">
        <Ico className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-extrabold mt-0.5 tabular-nums">
        {value ?? "—"}{unit && <span className="text-xs font-normal opacity-60 ml-1">{unit}</span>}
      </div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function EstadoBadge({ estado, parteAbierto }) {
  if (parteAbierto) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-emerald-500/25 text-emerald-700 dark:text-emerald-200 font-semibold">
        <Activity className="w-3 h-3 animate-pulse" /> En uso
      </span>
    );
  }
  const m = {
    disponible: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-200",
    en_taller: "bg-amber-500/25 text-amber-700 dark:text-amber-200",
    no_disponible: "bg-zinc-400/40 text-zinc-700 dark:text-zinc-300",
  };
  const labels = {
    disponible: "Disponible",
    en_taller: "En taller",
    no_disponible: "No disp.",
  };
  return <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-semibold ${m[estado] || m.no_disponible}`}>{labels[estado] || estado}</span>;
}
